import { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { formatRelativeTime } from "@/components/missions/MissionRow";
import { Badge } from "@/components/ui/badge/Badge";
import { ShareButton } from "@/components/ui/side-panel";
import {
  type Alert,
  type Audit,
  type RuleCheckResult,
  type ScreeningDocument,
} from "@/services/api/guardiansv2/types";
import {
  alertBadgeLabel,
  alertBadgeColor,
  guardianCheckFocusRef,
} from "@/services/api/guardiansv2";
import * as GuardiansV2API from "@/services/api/guardiansv2";
import type { ScopeLabelCache } from "../audits/useScopeLabelCache";
import { AlertInspectorContext } from "./AlertInspectorContext";
import type { FullAuditProgress } from "../auditLaunch/guardiansAuditLaunchStore";
import type { AuditPersonaTabId } from "../audits/AuditPersonaTabs";
import { useGuardiansAuditLaunch } from "../auditLaunch/GuardiansAuditLaunchContext";
import { AlertScreeningThread } from "./AlertScreeningThread";
import { GuardianAnalysisPanel } from "../audits/GuardianAnalysisPanel";
import { InvestigateSplitButton } from "../shared/InvestigateSplitButton";
import {
  panelActionOutlineClass,
  panelActionRowClass,
  panelActionSecondaryClass,
  panelActionSecondaryMutedClass,
} from "../shared/panelActionStyles";
import { IgnoreAlertModal } from "./IgnoreAlertModal";
import { canIgnoreAlert } from "./canIgnoreAlert";
import { alertConversationScope } from "./alertConversationParams";
import { useOpenGuardiansConversation } from "../conversation/useOpenGuardiansConversation";

function trimmed(value: string | null | undefined): string | null {
  const text = value?.trim();
  return text ? text : null;
}

function joinMetaParts(parts: Array<string | null | undefined>): string | null {
  const filtered = parts.map(trimmed).filter((part): part is string => part != null);
  return filtered.length > 0 ? filtered.join(" · ") : null;
}

export interface AlertPanelProps {
  alert: Alert;
  scopeLabels: ScopeLabelCache;
  shareUrl: string;
  onClose: () => void;
  onArchive: () => void;
  onAuditIssue: (userFocus?: string) => void;
  auditIssueProgress?: FullAuditProgress;
  linkedAuditId?: string | null;
  onEditRule?: (catalogueKey: string, ruleId: string) => void;
}

export function AlertPanel({
  alert,
  scopeLabels,
  shareUrl,
  onClose,
  onArchive,
  onAuditIssue,
  auditIssueProgress,
  linkedAuditId,
  onEditRule,
}: AlertPanelProps) {
  const openConversation = useOpenGuardiansConversation();
  const { isAuditInFlight } = useGuardiansAuditLaunch();
  const handleOpenCheck = useCallback(
    (check: RuleCheckResult) => {
      const { missionId, taskId } = alertConversationScope(alert);
      openConversation({
        missionId,
        taskId,
        focus: guardianCheckFocusRef(check.rule_id),
        title: alert.title ?? "Conversation",
      });
    },
    [alert, openConversation]
  );
  const displayTitle = trimmed(alert.title) ?? trimmed(alert.summary) ?? "Alert";
  const badgeColor = alertBadgeColor(alert);
  const auditIssueActive = auditIssueProgress?.active ?? false;
  const auditIssuePercent = auditIssueProgress?.percent ?? 0;
  const resolvedAuditId = linkedAuditId ?? alert.audit_id ?? null;

  const [screening, setScreening] = useState<ScreeningDocument | null>(null);
  const [screeningLoading, setScreeningLoading] = useState(false);
  const [audit, setAudit] = useState<Audit | null>(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [personaTab, setPersonaTab] = useState<AuditPersonaTabId>("ace");
  const [ignoreOpen, setIgnoreOpen] = useState(false);
  const [investigationRequested, setInvestigationRequested] = useState(false);

  const screeningId = alert.screening_id;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setScreeningLoading(true);
      try {
        let doc: ScreeningDocument;
        if (screeningId) {
          doc = await GuardiansV2API.getScreening(screeningId);
        } else {
          doc = await GuardiansV2API.getScreeningByAlert(alert._id);
        }
        if (!cancelled) setScreening(doc);
      } catch {
        if (!cancelled) setScreening(null);
      } finally {
        if (!cancelled) setScreeningLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [alert._id, screeningId]);

  useEffect(() => {
    if (!resolvedAuditId) {
      setAudit(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setAuditLoading(true);
      try {
        const doc = await GuardiansV2API.getAudit(resolvedAuditId);
        if (!cancelled) setAudit(doc);
      } catch {
        if (!cancelled) setAudit(null);
      } finally {
        if (!cancelled) setAuditLoading(false);
      }
    };
    void load();
    const interval = auditIssueActive
      ? window.setInterval(() => void load(), 3000)
      : undefined;
    return () => {
      cancelled = true;
      if (interval) window.clearInterval(interval);
    };
  }, [resolvedAuditId, auditIssueActive]);

  useEffect(() => {
    if (auditIssueActive) {
      setPersonaTab("ace");
    }
  }, [auditIssueActive]);

  useEffect(() => {
    if (audit?.status === "completed") {
      setPersonaTab("synthesis");
      setInvestigationRequested(false);
    }
    if (audit?.status === "failed") {
      setInvestigationRequested(false);
    }
  }, [audit?.status]);

  const investigating = auditIssueActive || investigationRequested;
  const showInvestigation = Boolean(resolvedAuditId || investigating);
  const trackedInFlight = audit ? isAuditInFlight(audit._id) : investigating;

  const handleInvestigate = useCallback(
    (userFocus?: string) => {
      setInvestigationRequested(true);
      setPersonaTab("ace");
      onAuditIssue(userFocus);
    },
    [onAuditIssue]
  );

  const taskId =
    alert.scope.task_id ?? (alert.subject.type === "task" ? alert.subject.id : null);
  const taskLabel = taskId ? scopeLabels.taskLabel(taskId) : null;
  const missionId = alert.scope.mission_id;
  const missionLabel = missionId ? scopeLabels.missionLabel(missionId) : null;
  const scopeLabel = taskLabel ?? missionLabel;

  const headerContextMeta = useMemo(
    () => joinMetaParts([scopeLabel, formatRelativeTime(alert.created_at)]),
    [scopeLabel, alert.created_at]
  );

  return (
    <div className="flex h-full flex-col overflow-hidden" data-testid="alert-panel">
      <header className="shrink-0 border-b border-white/5 px-6 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-x-2">
              <h2 className="min-w-0 truncate text-lg font-semibold text-slate-100">{displayTitle}</h2>
              <Badge color={badgeColor} className="shrink-0 text-[10px] uppercase">
                {alertBadgeLabel(alert)}
              </Badge>
            </div>
            {headerContextMeta ? (
              <p
                className="mt-2 truncate text-sm text-slate-500 tabular-nums"
                data-testid="alert-header-context"
              >
                {headerContextMeta}
              </p>
            ) : null}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <ShareButton url={shareUrl} label="Copy link to this alert" />
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-2 text-slate-400 hover:bg-white/5 hover:text-slate-200"
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
        <div className="space-y-8">
          <AlertScreeningThread
            screening={screening}
            loading={screeningLoading}
            scopeLabels={scopeLabels}
            showContextHeader={false}
            description={alert.description ?? screening?.outcome?.description}
            nextStep={alert.next_step ?? screening?.outcome?.triage?.next_step}
            onEditRule={onEditRule}
            onOpenCheck={handleOpenCheck}
          />

          {showInvestigation ? (
            <section data-testid="alert-audit-findings">
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">
                Investigation
              </h3>
              <GuardianAnalysisPanel
                audit={auditLoading && !audit ? null : audit}
                pendingAuditId={resolvedAuditId ?? (investigating ? "pending" : null)}
                scope={alert.scope}
                scopeLabels={scopeLabels}
                investigating={investigating}
                trackedInFlight={trackedInFlight}
                personaTab={personaTab}
                onPersonaTabChange={setPersonaTab}
              />
            </section>
          ) : null}
        </div>

        <div className={clsx("mt-6 border-t border-white/5 pt-4", panelActionRowClass)}>
          <InvestigateSplitButton
            label={resolvedAuditId ? "Re-investigate" : "Investigate"}
            disabled={investigating}
            progressPercent={auditIssuePercent}
            onInvestigate={handleInvestigate}
            testIdPrefix="alert-panel-audit-issue"
          />
          <button
            type="button"
            onClick={() => {
              const { missionId, taskId } = alertConversationScope(alert);
              openConversation({
                missionId,
                taskId,
                title: alert.title ?? "Conversation",
              });
            }}
            className={panelActionSecondaryClass}
            data-testid="alert-panel-view-conversation"
          >
            View conversation
          </button>
          <button
            type="button"
            onClick={onArchive}
            className={panelActionSecondaryMutedClass}
          >
            Hide alert
          </button>
          {canIgnoreAlert(screening) ? (
            <button
              type="button"
              onClick={() => setIgnoreOpen(true)}
              className={panelActionOutlineClass}
              data-testid="alert-ignore-button"
            >
              Ignore
            </button>
          ) : null}
        </div>

        <IgnoreAlertModal
          alertId={alert._id}
          open={ignoreOpen}
          screening={screening}
          onClose={() => setIgnoreOpen(false)}
          onIgnored={onArchive}
        />

        <div className="mt-6 border-t border-white/5 pt-4">
          <AlertInspectorContext alert={alert} scopeLabels={scopeLabels} />
        </div>
      </div>
    </div>
  );
}
